import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import VODFooter from '../components/VODFooter';
import LegalNav from '../components/LegalNav';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      <Helmet>
        <title>Cookie Policy | LTV VOD - Video Streaming Platform</title>
        <meta name="description" content="Cookie Policy for LTV VOD streaming platform. Learn how we use cookies and similar technologies to enhance your viewing experience." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://ltvproduction.com/vod/cookie-policy" />
      </Helmet>

      <LegalNav />
      
      {/* Content */}
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="text-4xl font-bold mb-4">Cookie Policy</h1>
          <p className="text-gray-400 mb-2">LTV VOD - Video On Demand Platform</p>
          <p className="text-gray-500 mb-8">Last updated: March 2026</p>
          
          <div className="space-y-8 text-gray-300">
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. What Are Cookies?</h2>
              <p className="mb-4">
                Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.
              </p>
              <p>
                This Cookie Policy explains how LTV VOD ("we," "us," or "our") uses cookies and similar technologies when you use our video streaming platform.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Types of Cookies We Use</h2>
              
              <h3 className="text-xl font-medium text-white mb-3">2.1 Essential Cookies</h3>
              <p className="mb-4">
                These cookies are necessary for the website to function properly. They enable core functionality such as:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>User authentication and account login</li>
                <li>Session management</li>
                <li>Security features</li>
                <li>Video playback functionality</li>
                <li>Payment processing</li>
              </ul>
              <p className="mb-6 text-sm text-gray-400">
                <strong>Note:</strong> Essential cookies cannot be disabled as they are required for the website to work.
              </p>
              
              <h3 className="text-xl font-medium text-white mb-3">2.2 Performance/Analytics Cookies</h3>
              <p className="mb-4">
                These cookies help us understand how visitors interact with our platform by collecting anonymous information:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Pages visited and time spent</li>
                <li>Video viewing statistics</li>
                <li>Error messages encountered</li>
                <li>Traffic sources</li>
              </ul>
              <p className="mb-6 text-sm">
                We use <strong>Google Analytics</strong> for this purpose. You can opt-out via{' '}
                <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">
                  Google Analytics Opt-out Browser Add-on
                </a>.
              </p>
              
              <h3 className="text-xl font-medium text-white mb-3">2.3 Functionality Cookies</h3>
              <p className="mb-4">
                These cookies remember your preferences and settings:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Language preferences</li>
                <li>Video quality settings</li>
                <li>Volume preferences</li>
                <li>Autoplay settings</li>
                <li>Watch history and continue watching position</li>
              </ul>
              
              <h3 className="text-xl font-medium text-white mb-3">2.4 Advertising Cookies</h3>
              <p className="mb-4">
                For users on our free tier, we display advertisements. Advertising cookies are used to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Deliver relevant advertisements based on your interests</li>
                <li>Limit the number of times you see an ad</li>
                <li>Measure the effectiveness of advertising campaigns</li>
                <li>Track conversions</li>
              </ul>
              <p className="mb-4">
                We use <strong>Google AdSense</strong> and <strong>Google Ad Manager</strong> for advertising. These services may place their own cookies.
              </p>
              <p className="text-sm">
                You can opt out of personalized advertising at{' '}
                <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">
                  Google Ads Settings
                </a>{' '}
                or visit{' '}
                <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">
                  Your Online Choices
                </a>.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Third-Party Cookies</h2>
              <p className="mb-4">We use services from the following third parties that may set their own cookies:</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4">Provider</th>
                      <th className="text-left py-3 px-4">Purpose</th>
                      <th className="text-left py-3 px-4">Privacy Policy</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    <tr>
                      <td className="py-3 px-4">Google Analytics</td>
                      <td className="py-3 px-4">Website analytics</td>
                      <td className="py-3 px-4">
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">Link</a>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Google AdSense</td>
                      <td className="py-3 px-4">Advertising</td>
                      <td className="py-3 px-4">
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">Link</a>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Stripe</td>
                      <td className="py-3 px-4">Payment processing</td>
                      <td className="py-3 px-4">
                        <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">Link</a>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Cloudflare</td>
                      <td className="py-3 px-4">Video delivery & security</td>
                      <td className="py-3 px-4">
                        <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">Link</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. How Long Do Cookies Last?</h2>
              <p className="mb-4">Cookies have different lifespans:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Session Cookies:</strong> Deleted when you close your browser</li>
                <li><strong>Persistent Cookies:</strong> Remain on your device for a set period or until deleted</li>
                <li><strong>Authentication Cookies:</strong> Valid for your login session (up to 30 days)</li>
                <li><strong>Preference Cookies:</strong> Up to 1 year</li>
                <li><strong>Analytics Cookies:</strong> Up to 2 years</li>
              </ul>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Managing Cookies</h2>
              <p className="mb-4">
                You can control and delete cookies through your browser settings. Here's how:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/delete-cookies-remove-info-websites-stored" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/windows/delete-and-manage-cookies-168dab11-0753-043d-7c16-ede5947fc64d" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:underline">Microsoft Edge</a></li>
              </ul>
              <div className="bg-yellow-900/20 border border-yellow-500/20 rounded-lg p-4">
                <p className="text-yellow-300 text-sm">
                  <strong>Warning:</strong> Disabling cookies may affect the functionality of LTV VOD. Some features like video playback, login persistence, and personalization may not work properly.
                </p>
              </div>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. GDPR & Your Rights (EU Users)</h2>
              <p className="mb-4">
                If you are located in the European Union, under GDPR you have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Be informed about how we use cookies (this policy)</li>
                <li>Consent to non-essential cookies before they are placed</li>
                <li>Withdraw consent at any time</li>
                <li>Request information about cookies we have set</li>
              </ul>
              <p>
                We display a cookie consent banner for EU visitors. You can change your preferences at any time.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Do Not Track</h2>
              <p>
                Some browsers include a "Do Not Track" (DNT) feature. We currently do not respond to DNT signals as there is no industry standard for compliance. However, you can use the cookie management options described above.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Changes to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time. Changes will be posted on this page with an updated "Last updated" date. For significant changes, we may provide additional notice.
              </p>
            </section>
            
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Contact Us</h2>
              <p className="mb-4">If you have questions about our use of cookies, please contact us:</p>
              <div className="bg-gray-900/50 rounded-lg p-6 space-y-2">
                <p><strong>LTV Production LTD</strong></p>
                <p>Email: <a href="mailto:privacy@ltvproduction.com" className="text-purple-400 hover:underline">privacy@ltvproduction.com</a></p>
                <p>Location: London, United Kingdom</p>
              </div>
            </section>
            
            {/* Related Links */}
            <section className="pt-8 border-t border-gray-800">
              <h3 className="text-lg font-semibold mb-4">Related Policies</h3>
              <div className="flex flex-wrap gap-4">
                <Link to="/vod/privacy-policy" className="text-purple-400 hover:underline">Privacy Policy</Link>
                <Link to="/vod/terms-of-service" className="text-purple-400 hover:underline">Terms of Service</Link>
                <Link to="/vod/content-policy" className="text-purple-400 hover:underline">Content Policy</Link>
              </div>
            </section>
          </div>
        </div>
      </main>
      
      <VODFooter />
    </div>
  );
};

export default CookiePolicy;
